<template>
    <section class="envelope-panel envelope-documents-panel">
        <header class="envelope-documents-header"><h2 class="text-base font-semibold">Documents in this Envelope</h2><span class="text-xs text-gray-500 whitespace-nowrap">{{ local_envelope_documents.length }} {{ local_envelope_documents.length === 1 ? 'document' : 'documents' }}</span></header>
        <div v-if="editable" class="envelope-documents-toolbar">
            <span class="text-xs text-blue-700" role="status">{{ toast }}</span>
            <button type="button" class="envelope-button" @click="showDocumentSelectModal">
                <file-plus-icon size="16" aria-hidden="true" />Select Documents
            </button>
        </div>
        <div class="envelope-documents-list">
            <draggable v-model="local_envelope_documents" :disabled="!editable" :animation="150" @start="onDocumentDragStart" @end="onDocumentDragEnd">
                <div v-for="(document, index) in local_envelope_documents" :key="`${document.type || 'document'}-${document.id || index}`" :data-id="document.id"
                    class="envelope-document-row flex items-center gap-2">
                    <file-icon size="16" class="flex-none text-gray-500" aria-hidden="true" />
                    <span class="flex-1 min-w-0 text-sm truncate" :title="document.display_filename">{{ document.display_filename || 'Unknown Document' }}</span>
                    <button v-if="editable" type="button" class="envelope-document-remove" @click="removeDocument(document)" :aria-label="`Remove ${document.display_filename || 'document'} from envelope`" title="Remove from envelope">
                        <x-icon size="14" aria-hidden="true" />
                    </button>
                </div>
            </draggable>
            <div v-if="!local_envelope_documents.length" class="px-4 py-6 text-center text-sm text-gray-500">
                No documents in this envelope.
            </div>
        </div>
        <footer class="envelope-documents-footer text-xs text-gray-500">
            <span v-if="editable">Choose documents from this transaction to add to the envelope.</span>
            <span v-else>Documents included in this envelope.</span>
        </footer>
        <envelope-document-select-modal :show="show_document_select_modal" :property_id="property_id" :property="property"
            :envelope_documents="envelope_documents" :documents="documents" :templates="templates" :document_folders="document_folders"
            @selected="handleDocumentSelection" @closed="closeDocumentSelectModal" />
    </section>
</template>

<script>
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import EnvelopeDocumentSelectModal from './envelope-document-select-modal.vue';
import ToastMessage from '../../../components/toast-message.vue';
import { FileIcon, FilePlusIcon, XIcon } from 'vue-feather-icons';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
    components: {
        ButtonBlueOutline,
        EnvelopeDocumentSelectModal,
        ToastMessage,
        FileIcon, FilePlusIcon, XIcon,
        draggable,
    },

    props: { read_only: { default: false },
        property_id: { required: true },
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        envelope: { default: null },
        envelope_documents: { default: null },
        toast: {},
    },

    data() {
        return {
            loading: false,
            show_document_select_modal: false,
            local_envelope_documents: this.envelope_documents || [],
            is_dragging: false,
        }
    },

    computed: {
        editable() {
            return !this.read_only && ! this.envelope?.sent_at;
        },
    },

    methods: {
        showDocumentSelectModal() {
            this.show_document_select_modal = true;
        },
        closeDocumentSelectModal() {
            this.show_document_select_modal = false;
        },
        handleDocumentSelection(selected_documents) {
            this.$emit('documents-updated', selected_documents);
        },
        removeDocument(document) {
            const index = this.local_envelope_documents.findIndex(d => d.id === document.id);

            if (index > -1) {
                this.local_envelope_documents.splice(index, 1);
            }

            this.$emit('documents-updated', this.local_envelope_documents);
        },
        onDocumentDragStart(event) {
            this.is_dragging = true;

            // Set cursor to move on the document body
            document.body.style.cursor = 'move';

            // Create a proper drag image that will follow the cursor
            if (event.dataTransfer && event.dataTransfer.setDragImage) {
                // Find the document div element
                let dragElement = event.target;
                while (dragElement && !dragElement.classList.contains('flex')) {
                    dragElement = dragElement.parentElement;
                }
                
                if (dragElement) {
                    // Create a clone of the element for the drag image
                    const dragImage = dragElement.cloneNode(true);
                    dragImage.style.cssText = `
                        position: absolute;
                        top: -1000px;
                        left: -1000px;
                        width: ${dragElement.offsetWidth}px;
                        height: ${dragElement.offsetHeight}px;
                        background: white;
                        border: 1px solid #3b82f6;
                        border-radius: 4px;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                        opacity: 0.9;
                        z-index: 9999;
                        pointer-events: none;
                    `;
                    
                    // Copy the content
                    dragImage.innerHTML = dragElement.innerHTML;
                    
                    document.body.appendChild(dragImage);
                    event.dataTransfer.setDragImage(dragImage, 10, 10);

                    // Clean up the drag image after a short delay
                    setTimeout(() => {
                        if (document.body.contains(dragImage)) {
                            document.body.removeChild(dragImage);
                        }
                    }, 100);
                }
            }
        },
        onDocumentDragEnd() {
            this.is_dragging = false;

            // Reset cursor to default
            document.body.style.cursor = 'default';

            // Emit the updated documents to parent
            this.$emit('documents-updated', this.local_envelope_documents);
        },
        getFolderName(folder_id) {
            if (!folder_id || !Array.isArray(this.document_folders)) {
                return 'Unknown Folder';
            }
            
            const folder = this.document_folders.find(f => f.id == folder_id);
            return folder ? folder.name : 'Unknown Folder';
        },
    },

    mounted() {
        this.local_envelope_documents = Array.isArray(this.envelope_documents)
            ? [...this.envelope_documents]
            : []
    },

    watch: {
        envelope_documents: {
            handler(newDocuments) {
                this.local_envelope_documents = Array.isArray(newDocuments) ? [...newDocuments] : [];
            },
            deep: true
        }
    },
}
</script>

<style scoped>
.envelope-documents-panel { display: flex; flex-direction: column; height: var(--envelope-panel-height, 24rem); }
.envelope-documents-header { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; min-height: 49px; flex-shrink: 0; padding: 0.5rem 0.75rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.envelope-documents-header h2 { margin: 0; }
.envelope-documents-toolbar { flex-shrink: 0; height: 52px; display: flex; align-items: center; justify-content: space-between; padding: 0.35rem 0.75rem; min-height: 52px; border-bottom: 1px solid #e5e7eb; }
.envelope-documents-list { flex: 1; min-height: 0; overflow-y: auto; }
.envelope-document-row { min-height: 2.625rem; padding: 0.45rem 0.75rem; border-bottom: 1px solid #f3f4f6; cursor: default; }
.envelope-document-row:hover { background: #f9fafb; }
.envelope-document-remove { display: inline-flex; align-items: center; justify-content: center; width: 1.5rem; height: 1.5rem; border: 1px solid #d1d5db; border-radius: 9999px; color: #6b7280; }
.envelope-document-remove:hover { color: #b91c1c; border-color: #b91c1c; }
.envelope-documents-footer { flex-shrink: 0; border-top: 1px solid #e5e7eb; padding: 0.625rem 0.75rem; }
</style>
